import { useEffect, useState } from 'react'

/** Below this the card stops being readable across a table. */
const MIN_FIT = 0.62

/**
 * Above this the card stops looking like a menu and starts looking like a
 * poster. A short menu on A4 would otherwise grow until four drinks filled a
 * whole sheet.
 */
const MAX_FIT = 1.6

/** Step of the search. Small enough that the settled size looks deliberate. */
const STEP = 0.04

/**
 * Sizes the menu card's type so it fills exactly one page.
 *
 * Measured rather than calculated: how much room a menu takes depends on where
 * every name wraps, which depends on the font the machine actually resolved.
 * The only reliable answer is to lay it out and look.
 *
 * The search runs in both directions. Shrinking alone was the obvious reading
 * of "fit on one page", but it left a short menu marooned at the top of an A4
 * sheet in type sized for a menu three times as long, the page fit, and
 * looked wrong. So a card with room to spare grows into it, and one that
 * overflows shrinks, and both stop at the same target: filling the page bar a
 * small margin.
 *
 * One step per frame rather than a loop, because reading scrollHeight after
 * changing font-size forces a synchronous reflow; a dozen back to back lock
 * the page up on a phone. Spread across frames the search is visible as the
 * preview settling, which is also honest about what is happening.
 *
 * `atFloor` is true when even the smallest size overflows, the caller warns
 * rather than printing a card with items missing off the bottom.
 */
export function useFitToPage(
  el: HTMLElement | null,
  /** Anything that changes the card's content or shape; restarts the search. */
  deps: unknown[],
): { fit: number; atFloor: boolean; measuring: boolean } {
  const [fit, setFit] = useState(1)
  const [atFloor, setAtFloor] = useState(false)
  const [measuring, setMeasuring] = useState(true)
  /**
   * How many steps this search has taken.
   *
   * A hard stop on the loop. The settling band is wider than one step, so in
   * principle the search always lands inside it, but that reasoning depends
   * on type scaling height proportionally, and a heading that re-wraps at one
   * size and not the next can break the assumption. Rather than trust it, the
   * search is capped: the full range is 25 steps, so 40 is generous and still
   * finite. Nothing about the card is wrong when this trips; it just stops.
   */
  const [steps, setSteps] = useState(0)

  // The dependency list is the caller's, spread into this hook's own, the
  // lint rule cannot see through that, and the alternative is serialising the
  // menu into a string on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const key = JSON.stringify(deps)

  useEffect(() => {
    setFit(1)
    setAtFloor(false)
    setMeasuring(true)
    setSteps(0)
  }, [key])

  useEffect(() => {
    if (!el || !measuring) return

    if (steps > 40) {
      setMeasuring(false)
      return
    }

    let cancelled = false
    const frame = requestAnimationFrame(() => {
      if (cancelled) return
      setSteps((n) => n + 1)

      // How tall the longest column's content actually is, asked of the
      // columns themselves.
      //
      // The container's own scrollHeight cannot answer this. A scroll
      // container never reports a scrollHeight below its clientHeight, so a
      // half-empty card measured exactly as full as one filled to the brim:
      //
      //   content 235px in a 612px body -> scrollHeight 612, spare 0
      //   content 487px in a 612px body -> scrollHeight 612, spare 0
      //
      // Every menu therefore looked like it was overflowing, the search ran
      // all the way to the floor, and the card printed at minimum size under a
      // "does not fit" warning with half the page blank. The columns are laid
      // out with `align-items: flex-start` so each is only as tall as its own
      // content, and asking them directly gives the real number in both
      // directions, 377px spare above, -379px short below.
      // Only the content columns are asked. The divider between them is
      // stretched to the full height of the page by design, so counting it
      // would report a full card every time, the very bug being fixed.
      const columns = el.querySelectorAll<HTMLElement>('[data-fit-column]')
      const needed = columns.length
        ? Math.max(...Array.from(columns, (c) => c.scrollHeight))
        : el.scrollHeight

      const spare = el.clientHeight - needed

      // The band the search settles in: past `breathing` from the bottom, but
      // not more than `slack` short of it. Without the upper bound the search
      // oscillates, one step up overflows, one step down leaves a gap, and it
      // alternates between them forever.
      const breathing = el.clientHeight * 0.02
      const slack = el.clientHeight * 0.08

      if (spare >= breathing && spare <= slack) {
        setMeasuring(false)
        return
      }

      if (spare < breathing) {
        // Overflowing, or close enough to the edge to look like it.
        if (fit <= MIN_FIT) {
          // The breathing room is given up before the warning is: a card that
          // fills the page exactly is still worth printing, and calling that
          // "does not fit" over 2% of margin would be false.
          setAtFloor(spare < 0)
          setMeasuring(false)
          return
        }
        setFit((f) => Math.max(MIN_FIT, f - STEP))
        return
      }

      // Room to spare: grow into it, up to the point where the card would stop
      // reading as a menu.
      if (fit >= MAX_FIT) {
        setMeasuring(false)
        return
      }
      setFit((f) => Math.min(MAX_FIT, f + STEP))
    })

    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [el, fit, measuring, steps, key])

  return { fit, atFloor, measuring }
}
